<template>
  <div class="collection-view">
    <ftu-block
      dismiss-key="collection"
      icon="view_carousel"
      title="This is your Collection"
      description="Collection is a real-time value tracking portfolio tool for your personal collection. Search our database of millions of cards and get advanced, accurate value estimations for any card when adding to your collection. You can even create multiple collections and share them with the Card Ladder community through the Showcase feature."
    />

    <page-header>
      <template #title>
        <align small-gap>
          <icon-button
            :icon="collection.public ? 'public' : 'lock'"
            :tooltip="collection.public ? 'Showcased' : 'Private'"
            :class="{ success: collection.public }"
            @click="handleShare"
          />

          <h1 class="secondary-font">
            {{ collectionName }}
          </h1>

          <dropdown
            v-if="collectionOptions.length"
            align="center"
            :active="menuOpen"
            @close="menuOpen = false"
          >
            <div>
              <icon-button icon="expand_more" @click="menuOpen = !menuOpen" />
            </div>

            <template #menu>
              <dropdown-menu
                :options="collectionOptions"
                @select="handleSelect"
              />
            </template>
          </dropdown>
        </align>
      </template>

      <template #actions>
        <align>
          <icon-button
            v-if="collectionSelectedIds.length && collections.length > 1"
            tooltip="Move selected cards"
            icon="drive_file_move"
            @click="showMoveModal = true"
          />

          <icon-button
            v-if="collectionSelectedIds.length"
            tooltip="Delete selected cards"
            icon="delete_outline"
            :loading="deleting"
            @click="deleteSelectedCards"
          />

          <icon-button
            icon="analytics"
            :tooltip="display === 'cards' ? 'View Collection Stats' : 'Go Back to Cards'"
            :class="{ success: display === 'stats' }"
            @click="display = (display === 'stats') ? 'cards' : 'stats'"
          />

          <icon-button
            tooltip="Collection Settings"
            icon="settings"
            @click="showSettingsModal = true"
          />

          <icon-button
            v-if="showAdd"
            tooltip="Add Card"
            class="add-card"
            icon="add_circle"
            @click="addCard"
          />
        </align>
      </template>
    </page-header>

    <empty-state
      v-if="showEmptyState"
      icon="view_carousel"
      title="You don't have any cards in this collection!"
      description="Cards can be added by clicking the Add button above"
    />

    <template v-else>
      <div class="wrapper">
        <collection-cards-view v-show="display === 'cards'" />
        <collection-stats-view v-if="display === 'stats'" />
      </div>
    </template>

    <collection-create-modal
      :open="showCreateModal"
      @close="showCreateModal = false"
      @create="switchCollection"
    />

    <collection-settings-modal
      :open="showSettingsModal"
      @close="showSettingsModal = false"
      @deleted="handleDeletedCollection"
    />

    <collection-upload-modal
      :open="showUploadModal"
      @close="showUploadModal = false"
    />

    <collection-move-cards-modal
      :open="showMoveModal"
      @close="showMoveModal = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { authService } from '@/services';

import { COLLECTION_SIZE_LIMIT, COLLECTION_COUNT_LIMIT } from '@cardladder/shared/constants';

import CollectionCardsView from './CollectionCardsView';
import CollectionCreateModal from './CollectionCreateModal';
import CollectionMoveCardsModal from './CollectionMoveCardsModal';
import CollectionSettingsModal from './CollectionSettingsModal';
import CollectionStatsView from './CollectionStatsView';
import CollectionUploadModal from './CollectionUploadModal';

export default {
  components: {
    CollectionCardsView,
    CollectionCreateModal,
    CollectionMoveCardsModal,
    CollectionSettingsModal,
    CollectionStatsView,
    CollectionUploadModal,
  },

  data() {
    return {
      showCreateModal: false,
      showSettingsModal: false,
      showUploadModal: false,
      showMoveModal: false,
      display: 'cards',
      menuOpen: false,
      deleting: false,
    };
  },

  watch: {
    '$route.query.id': {
      handler(value) {
        if (value) {
          const collection = this.collections.find(({ id }) => id === value);

          if (collection) {
            this.switchCollection(collection);
          }

          const query = { ...this.$route.query };
          delete query.id;
          this.$router.replace({ query });
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
      collections: (state) => state.collections,
      collectionSelectedIds: (state) => state.collectionSelectedIds,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    collectionName() {
      if (!this.collection || !this.collection.name) {
        return 'Collection';
      }

      return this.collection.name;
    },

    showEmptyState() {
      return this.collection.numCards === 0;
    },

    showAdd() {
      return this.collection.numCards < COLLECTION_SIZE_LIMIT;
    },

    collectionOptions() {
      const options = [];

      if (this.collections && this.collections.length) {
        this.collections.forEach((collection) => {
          if (collection.id !== this.collection.id) {
            options.push({
              value: collection.id,
              label: collection.name || 'Collection',
              icon: collection.public ? 'public' : 'lock',
            });
          }
        });
      }

      options.sort((a, b) => a.label?.localeCompare(b.label));

      if (this.isStaff || (this.collections && this.collections.length < COLLECTION_COUNT_LIMIT)) {
        options.push({ value: 'add', label: 'Create New Collection', icon: 'add' });
      }

      return options;
    },
  },

  methods: {
    handleSelect(value) {
      this.menuOpen = false;

      if (value === 'add') {
        this.showCreateModal = true;
      } else {
        const collection = this.collections.find(({ id }) => id === value);

        if (collection) {
          this.switchCollection(collection);
        }
      }
    },

    handleDeletedCollection(collectionId) {
      const collection = this.collections.find(({ id }) => id !== collectionId);

      if (collection) {
        this.switchCollection(collection);
      }
    },

    switchCollection(collection) {
      this.$store.commit('SET_COLLECTION', collection);
      this.$store.commit('SET_ACTIVE_COLLECTION_ID', collection.id);
      this.display = 'cards';

      if (this.$route.path !== '/collection') {
        this.$router.replace('/collection');
      }

      this.$bus.$emit('collection-changed');
    },

    async deleteSelectedCards() {
      if (window.confirm('Are you sure you want to delete the selected cards from your collection? All information about these cards will be deleted.')) {
        try {
          this.deleting = true;

          await Promise.all(this.collectionSelectedIds.map((id) => authService.removeCardFromCollection(this.user.uid, this.collection.id, id)));
        } catch (e) {
          this.$toasted.error('Error deleting these cards');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.deleting = false;
          this.$toasted.success('Cards have been deleted from the collection!');
          this.$bus.$emit('collection-changed');
        }, 4000);
      }
    },

    addCard() {
      this.$bus.$emit('collection-edit-card');
    },

    handleShare() {
      this.$bus.$emit('collection-share-setup');
    },
  },
};
</script>

<style lang="scss" scoped>
.collection-view {
  @include container;
  min-height: 100%;

  h4 {
    margin: 0;
  }
}

.add-card {
  margin-right: 0 !important;
}

.title {
  flex: 1;
  margin-bottom: 0;

  h1 {
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
